import { Injectable, signal } from '@angular/core';
import {
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { AuthUser, FirebaseService } from './firebase.service';

import type { IHttpClientLogin } from 'src/app/models';
import { StorageKey } from 'src/app/models';
import { assertTenantId } from 'src/app/utils/helpers';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class ClientUserService {
  /** The current or default tenantid for api */
  private tenantId = signal<number | null>(null);

  constructor(
    private readonly store: StorageService,
    private readonly fb: FirebaseService,
  ) {}

  /**
   * Checks and gets the CLIENT tenant id.
   *
   * @return {{ tenantId: number | null; }} General tenant id.
   */
  getTenantId(options?: { returnNullOnError: boolean }): Observable<{
    tenantId: number | null;
  }> {
    const tenantId = this.tenantId();

    if (tenantId) {
      return of({ tenantId });
    }

    if (options?.returnNullOnError) {
      return of({ tenantId: null });
    }

    return from(this.logout()).pipe(
      switchMap(() => {
        return throwError(
          () => new Error('Error determining user, log in again.'),
        );
      }),
    );
  }

  /**
   * Returns basic user information for account page and saves it to device
   * @returns {IHttpLogin | null} User data if user is found, otherwise null
   * @since 1.2.0
   */
  async handleLogin(user: IHttpClientLogin | null): Promise<void> {
    if (!environment.production) {
      console.log('getClientLogin(): /Login');
    }

    try {
      if (user) {
        await Promise.all([
          this.store.set(StorageKey.clientTenantId, user.tenantId),
          this.store.set(
            StorageKey.username,
            `${user.firstname} ${user.lastname}`.trim(),
          ),
        ]);

        this.tenantId.set(user.tenantId);
      }

      return;
    } catch (ex: any) {
      const loginError: any = ex?.message ?? '';
      if (loginError.includes('Unable to retrieve login')) {
        return;
      } else {
        await this.logout();
        return Promise.reject(ex ?? 'Unknown Exception - Login');
      }
    }
  }

  /**
   * Login - Token Guard
   *
   * Checks and gets the NORMAL type user IDs.
   *
   * @return {Promise<{ userId: number | null; tenantId: number | null; }>} General user IDs.
   * @since 3.1.0
   */
  async initTenantId(): Promise<{
    tenantId: number | null;
  }> {
    try {
      let tenantId = this.tenantId();
      if (tenantId) {
        return { tenantId };
      }

      tenantId = await this.store.getNumber(StorageKey.clientTenantId);

      assertTenantId(tenantId);

      this.tenantId.set(tenantId);

      return { tenantId };
    } catch (ex) {
      return { tenantId: null };
    }
  }

  /**
   * Get the token from firebase
   * @param forceRefresh force a token refresh - used after changing MFA state
   * so the server-rewritten custom claims land in the token immediately
   * @returns
   */
  async getUserToken(forceRefresh = false): Promise<string | null> {
    return this.fb.getToken(forceRefresh);
  }

  /**
   * Get the token from firebase
   * @returns
   */
  async isLoggedIn(): Promise<boolean> {
    return firstValueFrom(this.fb.isLoggedIn$);
  }

  /**
   * Get the currently signed-in Firebase user
   * @returns {AuthUser | null}
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    return firstValueFrom(this.fb.user$);
  }

  /**
   * Send the Firebase email-verification link to the current user
   */
  async sendVerificationEmail(): Promise<void> {
    return this.fb.sendVerificationEmail();
  }

  /**
   * Get Firebase Auth Email
   * @returns {string | null} email
   */
  getEmail(): Observable<string | null> {
    return this.fb.user$.pipe(map((user) => user?.email || null));
  }

  /**
   * Log user out and reset UserService
   */
  async logout(): Promise<void> {
    await this.store.clear();
    await this.fb.signOut();
    this.tenantId.set(null);
  }
}
